import { useEffect, useState } from 'react'

// Light/dark theme, driven by a `data-theme` attribute on <html> that the token block in index.css
// keys off. The initial value is set by a no-flash inline script in index.html (reads localStorage,
// else the OS preference) BEFORE first paint — this module just reads + flips it at runtime.
// Reusable verbatim across TrackingMCP / LoadingMCP (same token contract).

export type Theme = 'light' | 'dark'

export function getTheme(): Theme {
  if (typeof document !== 'undefined') {
    const t = document.documentElement.dataset.theme
    if (t === 'light' || t === 'dark') return t
  }
  return 'dark'
}

export function applyTheme(t: Theme): void {
  const d = document.documentElement
  d.dataset.theme = t
  d.style.colorScheme = t
  // Keep the inline root background (set pre-paint by the no-flash script in index.html) in sync, so
  // the viewport/overscroll area outside the body switches with the theme instead of staying pinned
  // to the initial colour. Mirrors the script's values.
  d.style.background = t === 'light' ? '#f6f8fb' : '#080b0f'
  try {
    localStorage.setItem('theme', t)
  } catch {}
}

export function useTheme(): [Theme, () => void] {
  const [theme, setTheme] = useState<Theme>(getTheme)
  // Follow <html data-theme> wherever it is written: the pre-paint script, Nav's route theme on a
  // client-side navigation (/dashboard dark -> / light), the Schedules admin's own theme. Reading it once
  // at mount left the toggle showing the other glyph after any such navigation.
  useEffect(() => {
    setTheme(getTheme())
    if (typeof MutationObserver === 'undefined') return
    const obs = new MutationObserver(() => setTheme(getTheme()))
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] })
    return () => obs.disconnect()
  }, [])
  const toggle = () => {
    const next: Theme = theme === 'dark' ? 'light' : 'dark'
    applyTheme(next)
    setTheme(next)
  }
  return [theme, toggle]
}
